import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { http } from '@/shared/api/client';
import { setAppCurrency, setAppDateFormat } from '@/shared/lib/format';

import { useAuthStore } from './auth-store';

/**
 * Loads app-wide display preferences (currency + date format) for every
 * authenticated subtree — including full-screen routes like the POS that
 * live outside the AppShell. Values are applied inside the fetch so the
 * re-render that follows already sees them; keying the Outlet forces that
 * re-render through the whole subtree.
 */
function useAppPrefs() {
  const token = useAuthStore((s) => s.token);
  return useQuery<{ code: string; symbol: string; date_format?: string }>({
    queryKey: ['app', 'currency'],
    enabled: !!token,
    queryFn: async () => {
      const { data } = await http.get('/api/app_currency');
      setAppCurrency(data?.code);
      setAppDateFormat(data?.date_format ?? '');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Wrap protected route subtrees. Redirects unauthenticated users to /login. */
export function RequireAuth() {
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  const prefs = useAppPrefs();

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return (
    <Outlet key={`${prefs.data?.code ?? ''}-${prefs.data?.date_format ?? ''}`} />
  );
}
